<template>
  <div class="infinite-carousel" :class="{ disabled, dots }">
    <div class="infinite-head" :class="{ container: title }">
      <slot name="heading" class="heading" />
      <component
        :is="heading" v-show="title" class="h4"
        :class="headingClass" v-html="title"
      />
      <slot v-if="exchanged" name="mobile-description" class="mobile description" />

      <div v-show="!disabled" class="controls">
        <CaretLeftCircle @click="() => prev(1)" />
        <CaretRightCircle @click="() => next(1)" />
      </div>
    </div>
    <slot v-if="!exchanged" name="description" class="description" />
    <div class="carousel">
      <div
        ref="inner" v-swipe.right="() => prev(1)" v-swipe.left="() => next(1)"
        class="inner" :style="!disabled ? innerStyles : {}"
      >
        <slot name="slide" />
      </div>
    </div>
    <div v-if="dots" class="dots">
      <div
        v-for="(uid, idx) in uniqueIds" :key="idx" class="dot"
        :class="{ active: uid === currentSlide.uid }"
        @click="() => goTo(uid, idx)"
      />
    </div>
  </div>
</template>

<script>
/* eslint-disable no-param-reassign */
import events from '@itbpbg/lms-components/src/mixins/events';
import CaretLeftCircle from '../assets/svg/caret-left-circle.svg?vue-component';
import CaretRightCircle from '../assets/svg/caret-right-circle.svg?vue-component';

export default {
  components: {
    CaretLeftCircle,
    CaretRightCircle,
  },
  mixins: [events],
  props: {
    autoRun: { type: Number, default: 0, required: false },
    exchanged: { type: Boolean, required: false },
    align: { type: String, default: 'center', required: false },
    title: { type: String, default: '', required: false },
    heading: { type: String, default: 'p', required: false },
    headingClass: { type: String, default: '', required: false },
    offset: { type: Number, default: 2, required: false },
    calculationsParentSelector: { type: String, default: '', required: false },
    dots: Boolean,
    uniqueIds: { type: Array, default: () => ([]), required: false },
    disabled: Boolean,
  },
  data() {
    return {
      innerStyles: {},
      step: '',
      transitioning: false,
      center: 0,
      screenWidth: 0,
      autoRunTimer: null,
      carouselHovered: false,
      currentSlide: {},
    };
  },
  computed: {
    slides() {
      return this.$slots.slide;
    },
  },
  watch: {
    // eslint-disable-next-line func-names
    '$store.state.layout': function () {
      this.$nextTick(() => this.setStep(true));
    },
  },
  mounted() {
    this.addEventListeners([
      [window, 'resize', () => this.setStep()],
    ]);
    this.$nextTick(() => this.setStep(true));

    if (this.autoRun) {
      const carousel = this.$el.querySelector('.carousel');
      this.addEventListeners([
        [carousel, 'mouseenter', () => {
          this.carouselHovered = true;
          clearTimeout(this.autoRunTimer);
        }],
        [carousel, 'mouseleave', () => {
          this.carouselHovered = false;
          this.autoRunCarousel();
        }],
      ]);
      setTimeout(() => {
        this.autoRunCarousel();
      }, 500);

      this.$once('hook:beforeDestroy', () => clearTimeout(this.autoRunTimer));
    }
  },
  methods: {
    goTo(uid, idx) {
      if (uid === this.currentSlide.uid) return;

      const currentDotIndex = this.uniqueIds.findIndex((slide) => slide === this.currentSlide.uid);
      // find direction to move
      const direction = idx > currentDotIndex ? 'right' : 'left';

      const wrapper = this.$el.querySelector('.inner');
      const slides = [...wrapper.children];
      const index = slides.findIndex((slide, i) => (direction === 'left' || (direction === 'right' && i > this.currentSlide.index))
        && slide.dataset.uid === uid);

      const steps = Math.abs(direction === 'right' ? index - this.currentSlide.index : this.currentSlide.index - index);

      // move to direction
      if (direction === 'left') {
        this.prev(steps);
      } else {
        this.next(steps);
      }
    },
    findCurrentSlide() {
      const wrapper = this.$el.querySelector('.inner');
      const wrapperBounds = wrapper.getBoundingClientRect();
      for (let idx = 0; idx < wrapper.children.length; idx += 1) {
        const slide = wrapper.children[idx];
        const slideBounds = slide.getBoundingClientRect();
        const slideOffset = slideBounds.left - wrapperBounds.left;

        const currentOffset = +this.innerStyles.transform.replace('translateX(', '').replace('px)', '') * -1;

        if (Math.floor(slideOffset) === Math.floor(currentOffset)) {
          this.currentSlide = { uid: slide.dataset.uid, index: idx };
        }
      }
    },
    autoRunCarousel() {
      if (this.autoRun && !this.carouselHovered) {
        this.autoRunTimer = setTimeout(() => {
          this.next(1);
        }, this.autoRun * 1000);
      }
    },
    setStep(force = false) {
      if (this.screenWidth !== window.innerWidth || force) {
        this.screenWidth = window.innerWidth;
        this.$nextTick(() => {
          const offset = this.offset || 0;
          const wrapper = this.$el.querySelector('.inner');
          const lastSlide = wrapper.lastChild;
          wrapper.insertBefore(lastSlide, wrapper.firstChild);
          const slide = this.slides[0].elm;
          const style = slide.currentStyle || window.getComputedStyle(slide);
          const slideWidth = slide.offsetWidth;
          let parentWidth = document.body.clientWidth;
          if (this.calculationsParentSelector) {
            parentWidth = document.querySelector(this.calculationsParentSelector).clientWidth;
          }
          const halfScreen = parentWidth / 2;
          const slideOffset = +style.marginRight.replace('px', '');
          const elementFullWidth = slideWidth + slideOffset;
          const elementsFittedInHalfScreen = Math.ceil(halfScreen / elementFullWidth);
          const exactWidthOfElementsToCenter = elementsFittedInHalfScreen * elementFullWidth + (slideWidth / 2);

          this.step = slideWidth + slideOffset;

          // centered alignment (default)
          this.center = exactWidthOfElementsToCenter - halfScreen;

          // container alignment
          if (this.align === 'container') {
            this.center = (((parentWidth - document.querySelector('main .container').offsetWidth) / 2) - (this.step * (offset + 1))) * -1;
          }

          // left alignment
          if (this.align === 'left') {
            this.center = 0;

            if (offset) {
              this.center = (this.step * offset);
            }
          }

          this.resetTranslate();
          this.findCurrentSlide();
        });
      }
    },
    next(steps = 1) {
      if (!this.$refs.inner) return;
      if (this.transitioning || (this.autoRun && !this.autoRunTimer)) return;
      this.transitioning = true;

      clearTimeout(this.autoRunTimer);
      this.moveLeft(steps);

      this.$emit('slide-changed');

      this.afterTransition(() => {
        this.resetTranslate();

        if (this.transitioning) {
          const wrapper = this.$el.querySelector('.inner');
          for (let i = 0; i < steps; i += 1) {
            const slide = wrapper.firstChild;
            slide.remove();
            wrapper.appendChild(slide);
          }
        }
        this.transitioning = false;

        this.findCurrentSlide();

        if (this.autoRun) {
          this.autoRunCarousel();
        }
      });
    },
    prev(steps = 1) {
      if (!this.$refs.inner) return;
      if (this.transitioning) return;
      this.transitioning = true;
      clearTimeout(this.autoRunTimer);

      const wrapper = this.$el.querySelector('.inner');
      const clones = [];
      for (let i = 0; i < steps; i += 1) {
        const lastSlide = wrapper.children[wrapper.children.length - (1 + i)];
        const clone = lastSlide.cloneNode(true);
        clones.push(clone);
        wrapper.insertBefore(clone, wrapper.firstChild);
      }

      clones.forEach((clone) => {
        clone.style.position = 'absolute';
        clone.style.right = '100%';
        clone.style.left = 'auto';
      });

      this.moveRight(steps);

      this.$emit('slide-changed');

      this.afterTransition(() => {
        this.resetTranslate();

        if (this.transitioning) {
          for (let i = 0; i < steps; i += 1) {
            const clone = clones[i];
            const lastSlide = wrapper.lastChild;
            lastSlide.remove();
            clones[i].style.left = clone.style.left;
            clones[i].style.removeProperty('right');
            clones[i].style.removeProperty('position');
          }
        }
        this.transitioning = false;

        this.findCurrentSlide();

        if (this.autoRun) {
          this.autoRunCarousel();
        }
      });
    },
    moveLeft(steps = 1) {
      const value = (+this.step * steps);

      this.innerStyles = {
        transform: `translateX(-${this.center + value}px)`,
      };
    },
    moveRight(steps = 1) {
      const value = (+this.step * steps);

      this.innerStyles = {
        transform: `translateX(${(this.center - value) * -1}px)`,
      };
    },
    afterTransition(callback) {
      const listener = (e) => {
        // callback only when ending transition is transform and is on transitioned div
        if (e.propertyName === 'transform' && e.target.classList.contains('inner')) {
          callback();
          this.$refs.inner.removeEventListener('transitionend', listener);
        }
      };
      this.$refs.inner.addEventListener('transitionend', listener);
    },
    resetTranslate() {
      if (this.center < 0) {
        this.center *= -1;
      }

      this.innerStyles = {
        transition: 'none',
        transform: `translateX(-${this.center}px)`,
      };
    },
  },
};
</script>

<style lang="scss">
.infinite-carousel {
  .description {
    &.mobile {
      display: none;
    }
  }

  .controls {
    text-align: center;
    margin: var(--spacing-24) 0 var(--spacing-36);

    svg {
      cursor: pointer;

      circle[fill] {
        fill: transparent;
      }

      circle[stroke] {
        stroke: var(--main-light);
      }

      path[stroke] {
        stroke: var(--main-light);
      }

      &:first-of-type {
        margin-right: var(--spacing-14);
      }
    }
  }

  .carousel {
    margin: 0 auto;
    width: 100%;
    overflow: hidden;

    .inner {
      transition: transform 1s;
      white-space: nowrap;
      text-align: center;

      > * {
        margin-right: 10px;
        border-radius: 4px;
        align-items: center;
        justify-content: center;
        display: inline-flex;
        overflow: hidden;
        white-space: normal;
        text-align: left;

        > * {
          flex: auto;
        }
      }
    }

    /* optional */
    button {
      margin-right: 5px;
      margin-top: 10px;
    }
  }

  .dots {
    display: flex;
    justify-content: center;
    margin-top: var(--spacing-16);

    .dot {
      flex: 0 0 auto;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background-color: var(--main-dark-light-3);

      &:not(.active) {
        cursor: pointer;
      }

      &.active {
        background-color: var(--main-light-shade-4);
      }

      + .dot {
        margin-left: 16px;
      }
    }
  }
}
</style>
